import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { Plus, Search, Trash2 } from "lucide-react";
import SummaryApi from "../../../common/SummaryApi";
import api from "../../../common/apiClient";
import ModernTable from "../../../components/ui/ModernTable";
import PageLoader from "../../../helpers/PageLoader";
import CaseCategoryDrawer from "../../../components/HRComponents/CaseCategoryDrawer";

export default function CaseCategoryList() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Categories தரவை சர்வரிலிருந்து பெறல்
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const url = SummaryApi.getAllCaseCategories?.url || "/api/case-categories/view-all";
      const res = await api.get(url);
      if (res.data.success) {
        setCategories(res.data.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // ─── திருத்தம் 1: handleDeleteCategory ஃபங்ஷனை useCallback கொண்டு சுற்றுகிறோம் ───
  const handleDeleteCategory = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const res = await api.delete(SummaryApi.deleteCaseCategory?.url(id));
      if (res.data.success) {
        toast.success("Category deleted successfully.");
        loadCategories(); // பட்டியலை ரிஃப்ரெஷ் செய்தல்
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete category.");
    }
  }, [loadCategories]);

  // தேடல் வடிகட்டி (Client-side Search)
  const filteredCategories = useMemo(() => {
    return categories.filter(cat => 
      cat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  // Pagination தரவு பிரிப்பு
  const paginatedCategories = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredCategories.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredCategories, currentPage]);

  // அட்டவணைக்கான Columns கட்டமைப்பு
  const columns = useMemo(() => [
    {
      key: "name",
      label: "Category Name",
      render: (row) => <span className="font-bold text-slate-800 whitespace-nowrap">{row.name}</span>
    },
    {
      key: "description",
      label: "Description",
      render: (row) => (
        <span className="text-slate-600 line-clamp-1 text-left">
          {row.description || <span className="text-slate-400 italic">No description provided</span>}
        </span>
      )
    },
    {
      key: "defaultAssigneeRole",
      label: "Default Assignee Routing",
      render: (row) => {
        const roles = {
          reporting_manager: "Direct Reporting Manager",
          hr: "Human Resources Team",
          it_admin: "IT Administration Desk"
        };
        return (
          <span className="px-2.5 py-1 text-[11px] font-semibold bg-slate-100 text-slate-700 rounded-full border border-slate-200 capitalize">
            {roles[row.defaultAssigneeRole] || row.defaultAssigneeRole?.replace("_", " ")}
          </span>
        );
      }
    }
  ], []);

  // ─── திருத்தம் 2: tableActions useMemo-ல் handleDeleteCategory-ஐ டிபென்டன்சியாக சேர்க்கிறோம் ───
  const tableActions = useMemo(() => [
    {
      label: "Delete",
      icon: <Trash2 className="w-4 h-4 text-red-500" />,
      onClick: (row) => handleDeleteCategory(row._id)
    }
  ], [handleDeleteCategory]);

  if (loading) return <PageLoader />;

  return (
    <>
    <div className="p-4 space-y-4 animate-in fade-in duration-300">
      
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="text-left">
          <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight">Case Categories Configuration</h1>
          <p className="text-xs text-slate-400 mt-1">Manage definitions and automatic assignment workflows for support categories.</p>
        </div>
        <div>
          <button 
            onClick={() => setIsDrawerOpen(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold h-9 shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Category
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      {/* <div className="bg-white border rounded-xl p-4 shadow-sm"> */}
        <div className="relative text-left">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // தேடலின் போது முதல் பக்கத்திற்கு நகர்த்துதல்
            }}
            placeholder="Search categories by name or description..."
            className="w-full border border-slate-200 rounded-lg pl-9 pr-4 py-1.5 text-xs outline-none focus:ring-1 focus:ring-red-200 h-9"
          />
        </div>
      {/* </div> */}

      {/* Categories Table */}
      <div className="">
        <div className="p-4 flex justify-between items-center bg-slate-50/50">
          <span className="font-bold text-slate-800 text-sm">Active Categories ({filteredCategories.length})</span>
        </div>

        <ModernTable 
          columns={columns}
          data={paginatedCategories}
          actions={tableActions}
          actionType="dropdown"
          selectable={false}
          pagination={{
            totalItems: filteredCategories.length,
            itemsPerPage: itemsPerPage,
            currentPage: currentPage,
            onPageChange: setCurrentPage
          }}
        />
      </div>

    </div>
      {/* Slide Drawer Component */}
      <CaseCategoryDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onSuccess={loadCategories} 
      />
    </>
  );
}